import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { DetectiveIcon, SpinnerGapIcon, ShieldCheckIcon, WarningIcon, LinkIcon, ArrowSquareOutIcon, TrophyIcon, CheckCircleIcon, XCircleIcon } from "@phosphor-icons/react";
import { fetchCabalScan } from "../lib/api";
import { getClientId } from "../lib/clientId";

const LEVEL = {
  clean: { color: "#00E599", bg: "rgba(0,229,153,0.08)", border: "rgba(0,229,153,0.4)" },
  warn: { color: "#F5A623", bg: "rgba(245,166,35,0.08)", border: "rgba(245,166,35,0.4)" },
  high: { color: "#FF3366", bg: "rgba(255,51,102,0.08)", border: "rgba(255,51,102,0.4)" },
  unknown: { color: "#8A94A6", bg: "rgba(138,148,166,0.08)", border: "rgba(138,148,166,0.35)" },
};
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");

const RiskMeter = ({ risk, level }) => {
  const c = LEVEL[level] || LEVEL.warn;
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-16 h-16 shrink-0" data-testid="cabal-risk-meter">
        <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke={c.color} strokeWidth="3"
            strokeDasharray={`${risk}, 100`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center font-heading font-black text-lg" style={{ color: c.color }}>
          {risk}
        </div>
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Cabal Risk</div>
        <div className="font-heading font-bold text-xl leading-tight" style={{ color: c.color }}>
          {risk >= 65 ? "🚨 " : risk < 18 ? "✅ " : "⚠️ "}
        </div>
      </div>
    </div>
  );
};

const CheckLine = ({ ok, label, detail }) => (
  <div className="flex items-center gap-2 text-sm" data-testid="community-check-line">
    {ok ? <CheckCircleIcon size={16} weight="fill" style={{ color: "#00E599" }} className="shrink-0" />
        : <XCircleIcon size={16} weight="fill" style={{ color: "#FF3366" }} className="shrink-0" />}
    <span className="text-white/90">{label}</span>
    {detail != null && <span className="ml-auto font-mono text-xs text-muted-foreground">{detail}</span>}
  </div>
);

const CommunityCheck = ({ c }) => {
  if (!c) return null;
  const lv = LEVEL[c.verdictLevel] || LEVEL.unknown;
  return (
    <div className="border p-3.5" style={{ background: lv.bg, borderColor: lv.border }} data-testid="community-runner-check">
      <div className="flex items-center gap-2 mb-2.5">
        <TrophyIcon size={18} weight="fill" style={{ color: c.verdictLevel === "clean" ? "#F0C000" : lv.color }} />
        <span className="font-heading font-bold text-sm">Community Runner Check</span>
        <span className="ml-auto font-heading font-bold text-sm" style={{ color: lv.color }} data-testid="community-verdict">{c.verdict}</span>
      </div>
      {!c.walletsVerified ? (
        <p className="text-xs font-mono text-muted-foreground">
          Couldn't reach the on-chain data source to verify holder wallets (it's at capacity right now). Token age check still applies below.
        </p>
      ) : null}
      <div className="space-y-1.5">
        <CheckLine ok={c.isNew} label={`New token (≤72h old)`} detail={c.ageHours != null ? `${c.ageHours}h old` : "—"} />
        {c.walletsVerified && (
          <>
            <CheckLine ok={c.healthyOk} label={`Top wallets funded ≥${c.minBalanceSol} SOL (not dust)`} detail={`${c.healthyCount}/${c.balChecked} ok`} />
            <CheckLine ok={c.agedOk} label={`Wallets aged ≥${c.minAgeDays}d (not fresh snipers)`} detail={`${c.agedCount}/${c.ageChecked} ok`} />
          </>
        )}
      </div>
    </div>
  );
};

export const CabalScan = ({ address, symbol }) => {
  const clientId = getClientId();
  const [data, setData] = useState(null);
  const scan = useMutation({
    mutationFn: () => fetchCabalScan(address, clientId),
    onSuccess: (d) => setData(d),
  });

  return (
    <div className="border border-[#FB7185]/25 bg-[#FB7185]/[0.04] p-5" data-testid="cabal-scan-panel">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 flex items-center justify-center bg-[#FB7185]/15 border border-[#FB7185]/40 text-[#FB7185] shrink-0">
          <DetectiveIcon size={20} weight="fill" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-lg leading-none">Cabal Scan</h2>
          <p className="text-[11px] text-muted-foreground mt-1">Who's really behind ${symbol || "this token"}? — shared funders, bundles & insider clusters.</p>
        </div>
      </div>

      {!data && (
        <button
          data-testid="cabal-scan-btn"
          onClick={() => scan.mutate()}
          disabled={scan.isPending}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold border border-[#FB7185]/50 bg-[#FB7185]/[0.08] text-[#FB7185] hover:bg-[#FB7185]/15 transition-colors disabled:opacity-60"
        >
          {scan.isPending ? <><SpinnerGapIcon size={16} className="animate-spin" /> Tracing wallets & funders…</> : <><DetectiveIcon size={16} weight="bold" /> Run Cabal Scan</>}
        </button>
      )}
      {scan.isError && <p className="mt-3 text-xs text-danger font-mono">Scan failed — try again in a moment.</p>}

      {data && (
        <div className="mt-4 space-y-4" data-testid="cabal-scan-result">
          {data.available === false ? (
            <p className="text-sm text-muted-foreground font-mono">No forensic data available for this token yet.</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 bg-black/30 border border-white/5 px-4 py-3">
                <div className="flex items-center gap-4">
                  <RiskMeter risk={data.cabalRisk} level={data.verdictLevel} />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Verdict</div>
                    <div className="font-heading font-bold text-lg leading-tight"
                      style={{ color: (LEVEL[data.verdictLevel] || LEVEL.warn).color }} data-testid="cabal-verdict">{data.verdict}</div>
                    {data.funderTraceAvailable && (
                      <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{data.holdersTraced} top wallets funding-traced</div>
                    )}
                  </div>
                </div>
              </div>

              <CommunityCheck c={data.community} />

              {/* signals */}
              <div className="space-y-1.5" data-testid="cabal-signals">
                {(data.signals || []).map((s, i) => {
                  const c = LEVEL[s.level] || LEVEL.warn;
                  const Icon = s.level === "clean" ? ShieldCheckIcon : s.key === "funder" ? LinkIcon : WarningIcon;
                  return (
                    <div key={i} className="flex items-start gap-2 px-3 py-2 border text-sm"
                      style={{ background: c.bg, borderColor: c.border }} data-testid={`cabal-signal-${s.key}`}>
                      <Icon size={16} weight="bold" style={{ color: c.color }} className="mt-0.5 shrink-0" />
                      <span className="text-white/90">{s.label}</span>
                    </div>
                  );
                })}
              </div>

              {/* funder clusters */}
              {(data.clusters || []).length > 0 && (
                <div className="space-y-2" data-testid="cabal-clusters">
                  <div className="text-[11px] uppercase tracking-wider text-[#FB7185] font-semibold">Shared-funder clusters</div>
                  {data.clusters.map((cl, i) => (
                    <div key={i} className="border border-[#FB7185]/30 bg-[#FB7185]/[0.05] px-3 py-2.5">
                      <div className="flex items-center gap-2 text-sm">
                        <LinkIcon size={14} weight="bold" className="text-[#FB7185]" />
                        <span className="font-mono text-white">{cl.count} wallets · ~{cl.supplyPct}% supply</span>
                        <span className="text-muted-foreground font-mono text-xs">via {short(cl.funder)}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {cl.wallets.map((w) => (
                          <span key={w} className="text-[10px] font-mono px-1.5 py-0.5 bg-white/5 border border-white/10 text-muted-foreground">{short(w)}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* dev funding trace */}
              {data.devFunding?.available && (
                <div className="border-t border-white/5 pt-3" data-testid="cabal-dev-funding">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Dev funding trace</div>
                  {data.devFunding.sharedRugCount > 0 ? (
                    <div className="flex items-start gap-2 px-3 py-2 border text-sm" style={{ background: LEVEL.high.bg, borderColor: LEVEL.high.border }}>
                      <WarningIcon size={16} weight="bold" className="text-danger mt-0.5 shrink-0" />
                      <div>
                        <span className="text-white/90">Dev wallet was funded by the same source as <b className="text-danger">{data.devFunding.sharedRugCount}</b> token(s) you flagged as rugs.</span>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {data.devFunding.matches.map((m) => (
                            <span key={m.mint} className="text-[10px] font-mono px-1.5 py-0.5 bg-danger/10 border border-danger/30 text-danger">💀 {m.symbol || short(m.mint)}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs font-mono text-muted-foreground">
                      Dev funded by {data.devFunding.funder ? short(data.devFunding.funder) : "unknown"} — no match to your flagged ruggers.
                    </p>
                  )}
                </div>
              )}

              {data.rugcheckUrl && (
                <a href={data.rugcheckUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-neon transition-colors">
                  <ArrowSquareOutIcon size={13} /> Full wallet graph on RugCheck
                </a>
              )}
              <p className="text-[10px] text-muted-foreground/70 font-mono">Heuristic signal, not proof. Solana-only. Always DYOR.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
